/** @jsx jsx */
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { useTheme, Typography, useMediaQuery } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

import { ICustomTheme, IChallengeList } from 'interfaces';
import ROUTES from 'constants/routes';
import { useSelector } from 'react-redux';
import { historySelectedChallengeSelector } from 'reducers/challenge.reducer';
import ActionPeriod from '../ActionPeriod';
import { StyledBubble } from 'components/pages/History/styles';

type Theme = {
  theme?: ICustomTheme;
};

const Container = styled.div(
  ({
    theme,
    isSelected,
    isMobile,
  }: {
    theme: ICustomTheme;
    isSelected: boolean | null;
    isMobile: boolean;
  }) => ({
    backgroundColor: theme.colors.white,
    transition: 'transform .3s ease-out',
    ...(!isMobile && isSelected && { transform: `translateX(1.5%)` }),
    ...(!isMobile && {
      '&:hover': {
        transform: `translateX(1.5%)`,
      },
    }),
    boxShadow: theme.shadows[2],
  }),
);

const ActionBody = styled.div({
  display: 'flex',
});

const LeftSection = styled.div(
  ({ isMobile, theme }: { isMobile: boolean; theme: ICustomTheme }) => ({
    flexGrow: 1,
    padding: theme.spacing(2, isMobile ? 0 : 3, 3, 3),
  }),
);

const Title = styled(Typography)(
  ({
    theme,
    isSelected,
  }: {
    theme: ICustomTheme;
    isSelected: boolean | null;
  }) => ({
    marginBottom: theme.spacing(0.5),
    marginTop: 0,
    wordBreak: 'break-word',
    color: isSelected
      ? theme.colors.paletteFromBO.main
      : theme.colors.grey.dark,
    transition: 'color .8s ease-out',
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    '&:first-letter': { textTransform: 'capitalize' },
  }),
);

const Declarations = styled.p<Theme>(({ theme }) => ({
  margin: 0,
  color: theme.colors.grey.dark,
  fontSize: theme.typography.body2.fontSize,
}));

const Moderations = styled.p<Theme>(({ theme }) => ({
  margin: theme.spacing(0.5, 0, 0),
  color: theme.colors.grey.dark,
  fontSize: theme.typography.caption.fontSize,
  fontStyle: 'italic',
}));

const RightSection = styled.div<Theme>(({ theme }) => ({
  width: 124,
  padding: theme.spacing(0, 3, 3, 0),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const StatusContainer = styled.div<Theme>(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginRight: theme.spacing(1.5),
}));

const Status = styled(Typography)(
  ({
    theme,
    isActionOnGoing,
  }: {
    theme: ICustomTheme;
    isActionOnGoing: boolean;
  }) => ({
    padding: theme.spacing(0.2, 1),
    minWidth: '14ch',
    color: theme.colors.white,
    fontSize: theme.typography.caption.fontSize,
    fontStyle: 'italic',
    backgroundColor: isActionOnGoing
      ? theme.colors.paletteFromBO.secondary
      : theme.colors.paletteFromBO.tertiary,
  }),
);

interface IActionProps {
  action: IChallengeList;
}

export default function Action({ action }: IActionProps) {
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const historySelectedChallenge = useSelector(
    historySelectedChallengeSelector,
  );

  let isSelected = null;
  if (historySelectedChallenge?.selectedChallengeId) {
    isSelected = action.id === +historySelectedChallenge?.selectedChallengeId;
  }

  return (
    <div
      css={{
        borderRadius: theme.shape.borderRadius,
        background: theme.colors.paletteFromBO.main,
        marginBottom: 1,
      }}
    >
      <Container isSelected={isSelected} isMobile={isMobile}>
        <RouterLink
          to={`${ROUTES.CHALLENGE}${ROUTES.HISTORY}/${action.id}`}
          css={{ textDecoration: 'none' }}
        >
          <StatusContainer>
            <Status isActionOnGoing={action.isActionOnGoing} align="center">
              {action.isActionOnGoing
                ? t('common.inProgress')
                : t('common.finished')}
            </Status>
          </StatusContainer>
          <ActionBody>
            <LeftSection isMobile={isMobile}>
              <Title isSelected={isSelected}>
                {`${action.title} - ${action.id}`}
              </Title>
              <ActionPeriod
                css={{
                  marginBottom: theme.spacing(1),
                  transition: 'color .8s ease-out',
                  fontSize: 12,
                  color: isSelected
                    ? theme.colors.paletteFromBO.main
                    : theme.colors.grey.dark,
                }}
                minified
                startDate={action.startDate}
                endDate={action.endDate}
              />
              <Declarations>
                {action.totalDeclaration}{' '}
                {t('common.declarations', { count: action.totalDeclaration })}
              </Declarations>
              {action.countDeclarationsWaiting > 0 && (
                <Moderations>
                  {t(
                    'challenges.declaration.history.declarations_being_moderated',
                    {
                      beingModeratedDeclarations:
                        action.countDeclarationsWaiting,
                    },
                  )}
                </Moderations>
              )}
            </LeftSection>
            {isMobile && (
              <RightSection>
                <StyledBubble
                  css={{
                    cursor: 'pointer',
                    backgroundColor: theme.colors.grey.dark,
                  }}
                >
                  <ArrowForwardIcon fontSize="large" />
                </StyledBubble>
              </RightSection>
            )}
          </ActionBody>
        </RouterLink>
      </Container>
    </div>
  );
}
